'use client'
import { CMSLink } from '@/components/Link'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel'
import { cn } from '@/utilities/ui'
import StyleListServiceCard from './cards/StylistServiceCard'
import { CardWithIconAndButton } from './cards/CardWithIconAndButton'
import Autoplay from 'embla-carousel-autoplay'
import { ImageOverlayButtonCard } from './cards/imageOverLayButtonCard'
import ServiceBlock2 from './ServiceBlock2'
import NormalCard from './cards/NormalCard'
import { ChevronsRight } from 'lucide-react'
import DefaultCard from './cards/DefaultCard'
import { ServicesThree } from './ServiceBlock3'
import React from 'react'
import BlockWrapper from '@/components/BlockWrapper'
import RichText from '@/components/RichText'

export const ServiceBlocksComponent = (props: any) => {
  const {
    title,
    services,
    hasCarousel,
    subtitle,
    hasCta,
    cta,
    showPrevNext,
    backgroundColor,
    cardType,
    cardStyle,
    iconPosition,
    imageStyle,
    imagePadding,
    imageClassNames,
    cardClassNames,
    type,
    eyebrow,
    hasContainer,
  } = props

  if (type === 'serviceBlock2') return <ServiceBlock2 {...props} />
  if (type === 'serviceBlock3') return <ServicesThree {...props} />

  const renderCard = (service: any, cardType: any) => {
    switch (cardType) {
      case 'serviceCard':
        return (
          <StyleListServiceCard
            service={service}
            cardStyle={cardStyle || 'padding'}
            iconPosition={iconPosition || 'left'}
            imageStyle={imageStyle || 'rounded'}
            imagePadding={imagePadding || 'normal'}
            imageClassName={imageClassNames}
            cardClassName={cardClassNames}
          />
        )
      case 'processCard':
        return (
          <CardWithIconAndButton
            description={service.description}
            title={service.title}
            icon={service.icon}
            links={service?.links}
            overlay={true}
            textColor={
              backgroundColor === 'none' ||
              backgroundColor === 'card' ||
              backgroundColor === 'muted'
                ? 'text-primary'
                : 'text-primary-foreground'
            }
            backgroundColor={backgroundColor}
          />
        )
      case 'overlayCard':
        return <ImageOverlayButtonCard {...service} />
      case 'card':
        return <NormalCard service={service} iconPosition={iconPosition || 'left'} />
      case 'defaultCard':
        return <DefaultCard service={service} iconPosition={iconPosition || 'left'} />
      default:
        return <DefaultCard service={service} iconPosition={iconPosition || 'left'} />
    }
  }

  const backgroundColorClass =
    backgroundColor === 'black'
      ? 'bg-[#181424]'
      : backgroundColor
        ? `bg-${backgroundColor}`
        : 'bg-[#181424]'

  return (
    <BlockWrapper className={cn('w-full', backgroundColorClass)} aria-label="Services">
      <div
        className={cn('rounded-xl', {
          'container mx-auto 2xl:px-0': hasContainer,
          'px-2': !hasContainer,
        })}
      >
        <header className="flex flex-col items-center justify-center mb-5">
          {eyebrow && (
            <div
              className={cn(
                'border border-primary text-primary px-4 py-1 rounded-full mb-2 w-fit flex items-center justify-center',
                {
                  'border-border text-primary-foreground': backgroundColor === 'primary',
                },
              )}
            >
              <span>{eyebrow}</span>
            </div>
          )}
          {title && (
            <RichText
              data={title}
              enableGutter={false}
              enableProse={false}
              className={cn('text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-3 md:mb-4 lg:mb-6 text-foreground', {
                'text-black':
                  backgroundColor === 'none' ||
                  backgroundColor === 'card' ||
                  backgroundColor === 'muted',
              })}
            />
          )}
          {subtitle && (
            <RichText
              data={subtitle}
              enableGutter={false}
              enableProse={false}
              className={cn('ttext-muted-foreground lg:text-lg max-w-3xl mx-auto text-center text-muted-foreground', {
                'text-muted':
                  backgroundColor === 'none' ||
                  backgroundColor === 'card' ||
                  backgroundColor === 'muted',
              })}
            />
          )}
        </header>

        {hasCarousel ? (
          <Carousel
            className="w-full h-full"
            plugins={[
              Autoplay({
                delay: 3000,
                stopOnInteraction: false,
                stopOnMouseEnter: true,
                stopOnFocusIn: true,
              }),
            ]}
            opts={{
              loop: true,
            }}
            aria-label="Service Carousel"
          >
            <CarouselContent>
              {services.map((fence: any, index: any) => (
                <CarouselItem
                  key={index}
                  className={cn('lg:basis-1/2 xl:basis-1/3 group flex', {
                    'xl:basis-1/4': !hasContainer,
                  })}
                  role="listitem"
                >
                  <article className="h-full flex flex-col w-full">
                    {renderCard(fence, cardType)}
                  </article>
                </CarouselItem>
              ))}
            </CarouselContent>
            {showPrevNext && (
              <>
                <CarouselPrevious />
                <CarouselNext />
              </>
            )}
          </Carousel>
        ) : (
          <ul
            className="grid grid-cols-1 md:grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-y-8 gap-x-5 auto-rows-fr"
            role="list"
          >
            {services.map((service: any, index: any) => (
              <li key={index} className="group h-full flex flex-col" role="listitem">
                <article className="h-full flex flex-col">{renderCard(service, cardType)}</article>
              </li>
            ))}
          </ul>
        )}

        {hasCta && (
          <nav className="flex flex-1 justify-center mt-10" aria-label="Services call to action">
            <div className="relative w-fit inline-flex overflow-hidden group/link rounded-full">
              <CMSLink
                {...cta}
                className="z-10 inline-flex items-center border border-primary bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground cursor-pointer font-semibold text-base tracking-wide transition-colors duration-300 rounded-full px-6 py-8"
                aria-label={cta.label || 'View all services'}
              >
                <div className="w-12 h-12 rounded-full flex flex-1 items-center justify-center ml-2 bg-primary text-primary-foreground">
                  <ChevronsRight />
                </div>
              </CMSLink>
              {/* Removed hover effect */}
            </div>
          </nav>
        )}
      </div>
    </BlockWrapper>
  )
}
